// src/App.jsx
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import RecoilNexus from 'recoil-nexus';
import { CartService } from './services/cartService';
import CartDrawer from './components/CartDrawer';
import WhatsAppButton from './components/WhatsAppButton';
import { ROUTES } from './constants';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastContainer } from 'react-toastify';
import brandingService from './services/brandingService';
import facebookConversionService from './services/facebookConversionService';
import CategoryController from './pages/admin/controllers/category-controller';
import 'react-toastify/dist/ReactToastify.css';

// Eagerly loaded pages (critical conversion path: product landing + confirmation)
import ProductPage from './pages/ecommerce/product/ProductPage';
import OrderConfirmation from './pages/ecommerce/order/OrderConfirmation';

// Lazy loaded public pages (not on the ad landing critical path)
const HomePage = lazy(() => import('./pages/ecommerce/home-page/HomePage'));
const Login = lazy(() => import('./pages/Login'));
const Catalog = lazy(() => import('./pages/ecommerce/catalog/Catalog'));
const OrderTrackingPage = lazy(() => import('./pages/ecommerce/order/OrderTrackingPage'));
const CheckoutPage = lazy(() => import('./pages/ecommerce/checkout/CheckoutPage'));

// Lazy loaded pages (admin / less critical)
const Signup = lazy(() => import('./pages/Signup'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const ProductsPage = lazy(() => import('./pages/admin/ProductsPage'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const DeliveryStatisticsPage = lazy(() => import('./pages/admin/statistics/DeliveryStatisticsPage'));
const ProductStatisticsPage = lazy(() => import('./pages/admin/statistics/ProductStatisticsPage'));
const ConfirmatriceStatisticsPage = lazy(() => import('./pages/admin/statistics/ConfirmatriceStatisticsPage'));
const ConfirmatricesPage = lazy(() => import('./pages/admin/confirmatrices/ConfirmatricesPage'));
const FacebookStatsPage = lazy(() => import('./pages/admin/facebook/FacebookStatsPage'));
const FacebookAdsWorkersPage = lazy(() => import('./pages/admin/facebook/FacebookAdsWorkersPage'));
const OrdersPage = lazy(() => import('./pages/admin/OrdersPage'));
const CustomersPage = lazy(() => import('./pages/admin/CustomersPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const StoresPage = lazy(() => import('./pages/admin/StoresPage'));
const AddProductPage = lazy(() => import('./pages/admin/AddProductPage'));
const EditProductPage = lazy(() => import('./pages/admin/EditProduct'));
const OrderDetailPage = lazy(() => import('./pages/admin/OrderDetailPage'));
const ReturnReceivedScanPage = lazy(() => import('./pages/admin/ReturnReceivedScanPage'));
const VariantsPage = lazy(() => import('./pages/admin/VariantsPage'));
const BrandsPage = lazy(() => import('./pages/admin/BrandsPage'));
const SuppliersPage = lazy(() => import('./pages/admin/SuppliersPage'));
const TvaPage = lazy(() => import('./pages/admin/TvaPage'));
const AddOrder = lazy(() => import('./pages/admin/new-order-components/AddOrder'));
const EditOrder = lazy(() => import('./pages/admin/edit-order-components/EditOrder'));
const Platform = lazy(() => import('./pages/admin/platform/platform'));
const Purchase = lazy(() => import('./pages/admin/purchase/Purchase'));
const PurchasesList = lazy(() => import('./pages/admin/purchase/PurchasesList'));
const EditPurchase = lazy(() => import('./pages/admin/purchase/EditPurchase'));
const SupplierConsumptionPage = lazy(() => import('./pages/admin/SupplierConsumptionPage'));
const SupplierPaymentsPage = lazy(() => import('./pages/admin/SupplierPaymentsPage'));
const Profile = lazy(() => import('./pages/ecommerce/profile/profile'));
const CategoryPage = lazy(() => import('./pages/admin/CategoryPage'));
const ChargePage = lazy(() => import('./pages/admin/ChargePage'));
const ExchangesPage = lazy(() => import('./pages/admin/exchanges/ExchangesPage'));
const AddExchange = lazy(() => import('./pages/admin/exchanges/AddExchange'));
const EditExchange = lazy(() => import('./pages/admin/exchanges/EditExchange'));
const ImportPage = lazy(() => import('./pages/admin/import/ImportPage'));
const PromoCodePage = lazy(() => import('./pages/admin/promocodes/PromoCodePage'));
const SocialMappingsPage = lazy(() => import('./pages/admin/social/SocialMappingsPage'));
const BarcodeVerificationPage = lazy(() => import('./pages/BarcodeVerificationPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));


const App = () => {
  useEffect(() => {
    // Load cart data from localStorage on app initialization
    CartService.loadCart();
    
    // Initialize branding service and apply CSS variables
    brandingService.init();

    // Load and inject category pixel scripts directly into <head>
    const loadCategoryPixels = async () => {
      try {
        const categories = await CategoryController.fetchAllCategories();
        const list = Array.isArray(categories) ? categories : categories.list || [];

        // Remove any previously injected category pixels
        document.querySelectorAll('[data-category-pixel]').forEach(el => el.remove());

        // ALLOWED: only <script> with src pointing to known pixel domains.
        // The Meta pixel code is skipped: facebookConversionService loads each category's
        // pixel itself (running the pasted code too would send every PageView twice, to every pixel).
        const ALLOWED_SCRIPT_DOMAINS = [
          'snap.licdn.com',
        ];
        const isMetaPixelCode = (node) =>
          /fbq\s*\(|fbevents|connect\.facebook\.net|facebook\.com\/tr/i.test(node.outerHTML || '');

        list.forEach(category => {
          if (!category.pixelBalise) return;
          const template = document.createElement('template');
          template.innerHTML = category.pixelBalise.trim();

          Array.from(template.content.childNodes).forEach(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            if (isMetaPixelCode(node)) return;
            const tag = node.tagName.toLowerCase();

            if (tag === 'script') {
              const src = node.getAttribute('src') || '';
              // Only allow scripts loading from known pixel domains
              const isTrustedSrc = src && ALLOWED_SCRIPT_DOMAINS.some(d => {
                try { return new URL(src).hostname.endsWith(d); } catch { return false; }
              });

              if (!isTrustedSrc) return; // skip untrusted scripts

              const fresh = document.createElement('script');
              Array.from(node.attributes).forEach(attr => fresh.setAttribute(attr.name, attr.value));
              if (node.textContent) fresh.text = node.textContent;
              fresh.setAttribute('data-category-pixel', category.id);
              document.head.appendChild(fresh);
            } else if (tag === 'noscript' || tag === 'img' || tag === 'meta') {
              const clone = node.cloneNode(true);
              clone.setAttribute('data-category-pixel', category.id);
              document.head.appendChild(clone);
            }
            // Ignore any other tags (div, iframe, etc.)
          });
        });
      } catch (error) {
        console.error('Failed to load category pixels:', error);
      }
    };

    // Defer non-critical category pixel loading so product page renders faster
    const deferPixels = typeof requestIdleCallback === 'function'
      ? requestIdleCallback
      : (cb) => setTimeout(cb, 1500);
    deferPixels(() => loadCategoryPixels());

    // Initialize Facebook Pixel
    facebookConversionService.initialize().catch(error => {
      console.error('Facebook Pixel initialization failed:', error);
    });
  }, []);

  return (
    <RecoilRoot>
      <RecoilNexus />
      <Router>
        {/* Facebook pixels are the products' category pixels: PageView/ViewContent
            are sent from the product page (facebookConversionService). */}
        {/* CartDrawer should be outside of Routes but inside Router */}
        <CartDrawer />
        {/* Floating WhatsApp contact button (public pages only) */}
        <WhatsAppButton />
        
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div></div>}>
        <Routes>
          {/* Public routes */}
          <Route path={ROUTES.home} element={<HomePage />} />
          <Route path={ROUTES.login} element={<Login />} />
          {/* <Route path={ROUTES.signup} element={<Signup />} /> */}
          <Route path="/product/:id/:platformid" element={<ProductPage />} />
          {/* <Route path="/catalog" element={<Catalog />} /> */}
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} /> {/* Add this route */}
          <Route path="/track-order/:orderId" element={<OrderTrackingPage />} />
          <Route path="/checkout" element={<CheckoutPage />} /> {/* Add Checkout route */}
          <Route path="/barcode-verification" element={<BarcodeVerificationPage />} /> {/* Add this line */}
          <Route path="/documentation" element={<DocumentationPage />} /> {/* Add Documentation route */}
          
          {/* Protected user routes - requires login but not admin */}
          <Route element={<ProtectedRoute adminOnly={false} />}>
            <Route path="/profile" element={<Profile />} />
          </Route>
          
          {/* Protected admin routes */}
          <Route element={<ProtectedRoute adminOnly={true} />}>
            <Route path={ROUTES.admin} element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="statistics/delivery" element={<DeliveryStatisticsPage />} />
              <Route path="statistics/products" element={<ProductStatisticsPage />} />
              <Route path="statistics/confirmatrices" element={<ConfirmatriceStatisticsPage />} />
              <Route path="confirmatrices" element={<ConfirmatricesPage />} />
              <Route path="facebook/stats" element={<FacebookStatsPage />} />
              <Route path="facebook/workers" element={<FacebookAdsWorkersPage />} />
              <Route path="facebook-stats" element={<Navigate to="/admin/facebook/stats" replace />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="categories" element={<CategoryPage />} />
              <Route path="charges" element={<ChargePage />} />
              <Route path="variants" element={<VariantsPage />} />
              <Route path="brands" element={<BrandsPage />} />
              <Route path="suppliers" element={<SuppliersPage />} />
              <Route path="tva" element={<TvaPage />} />
              <Route path="products/new" element={<AddProductPage />} />
              <Route path="products/:id/edit" element={<EditProductPage />} />
              <Route path="orders/:id" element={<OrderDetailPage />} />
              <Route path="orders/:id/edit" element={<EditOrder />} />
              <Route path="orders/new" element={<AddOrder />} />
              <Route path="/admin/purchases" element={<PurchasesList />} />
              <Route path="/admin/purchases/new" element={<Purchase />} />
              <Route path="/admin/purchases/:id" element={<EditPurchase />} />
              <Route path="/admin/supplier-payments" element={<SupplierPaymentsPage />} />
              <Route path="/admin/supplier-consumption" element={<SupplierConsumptionPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="return-received" element={<ReturnReceivedScanPage />} />
              <Route path="platform" element={<Platform />} />
                {/* Exchange routes */}
                <Route path="exchanges" element={<ExchangesPage />} />
                <Route path="exchanges/add" element={<AddExchange />} />
                <Route path="exchanges/:id/edit" element={<EditExchange />} />
              {/* <Route path="exchanges/:id" element={<ExchangeDetail />} */}
              <Route path="/admin/import" element={<ImportPage />} /> {/* Add the new route for ImportPage */}
              <Route path="/admin/promocodes" element={<PromoCodePage />} /> {/* Add this line */}
              <Route path="social-leads" element={<SocialMappingsPage />} /> {/* Social comment → abandoned order */}

              <Route path="customers" element={<CustomersPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="stores" element={<StoresPage />} />
            </Route>
          </Route>

          {/* Fallback route */}
          <Route path="*" element={<Navigate to={ROUTES.home} />} />
        </Routes>
        </Suspense>
  {/* ToastContainer should be here, outside Routes but inside Router */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Router>
    </RecoilRoot>
  );
};

export default App;
